import { useMemo } from "react";

import { t } from "i18next";

import type { IntegrationDescription } from "@smartsuite/automation-engine-api";

import type { CandidateStore } from "../../../../../../../stores/automation/candidate.store";
import type { BranchRef } from "../../../../../../../stores/automation/branches.store";
import type { LoopScope } from "../../../../../../../stores/automation/looping.store";
import type { ActionCandidate } from "../../../../../../../types/candidate";
import {
  ActionLabel,
  FIND_RECORD_LIMIT_ID,
} from "../../../../../../../utils/constants";
import {
  findInputAndPick,
  pickValueConfigLimitSort,
} from "../../../../../../../utils/inputs";
import {
  getActionName,
  isSupportedOutput,
} from "../../../../../Forms/InputForm/utils";
import type { LeftSource } from "./types";

const actionToLeftSource = (
  action: ActionCandidate,
  integrations: IntegrationDescription[],
): LeftSource | undefined => {
  if (action.actionReference === undefined) return undefined;
  // Skip actions that expose no condition-able record fields (e.g. Create a
  // comment / Send email): they would open an empty field submenu. Only
  // single-record-returning actions carry conditionFields worth offering.
  if (action.description.conditionFields.length === 0) return undefined;
  return {
    kind: "action",
    hash: action.hash,
    name: t("automation-form.fields-from-source", {
      name: getActionName(action, integrations),
    }),
    conditionFields: action.description.conditionFields,
    reference: {
      oneofKind: "actionReference",
      actionReference: action.actionReference,
    },
  };
};

/**
 * Current Item left source for a branch condition whose block sits inside a
 * loop. Only a find-records record-list loop iterates SINGLE records whose
 * `conditionFields` are addressable per item, so this mirrors the right-side
 * resolution in `convertLoopingSourceOptionsToDynamicSections`: resolve the
 * loop's record-list find-records action from `allActions` and expose its own
 * `conditionFields` + `actionReference`. The stored reference is a plain
 * action reference (outputId === conditionFieldId, source = the loop source
 * action) -- the same on-the-wire shape the right side uses for a find-records
 * loop's current-item field, which the engine scopes to the current item by
 * execution context, and which resolves straight back through
 * `resolveReferencedConditionField` so operators derive. Webhook/list loop
 * sources are not single-record entities and get no Current Item source.
 */
const currentItemLeftSource = (
  candidate: CandidateStore,
  loopScope: LoopScope,
): LeftSource | undefined => {
  const source = loopScope.source;
  if (
    source?.oneofKind !== "recordList" ||
    source.recordList.recordList.oneofKind !== "actionReference" ||
    source.recordList.recordList.actionReference.actionId !==
      ActionLabel.findRecords
  ) {
    // Also excludes FROM_TRIGGER-sourced loops (recordList.recordList.oneofKind
    // === "reference"): like webhook-sourced (outputReference) loops, they have
    // no per-item conditionFields to expose as a Current Item.
    return undefined;
  }

  const { instanceId } = source.recordList.recordList.actionReference;
  const sourceAction = candidate.allActions.find(
    (action) => action.actionReference?.instanceId === instanceId,
  );
  if (sourceAction?.actionReference === undefined) return undefined;
  // No condition-able fields -> nothing to offer (empty submenu).
  if (sourceAction.description.conditionFields.length === 0) return undefined;

  return {
    kind: "current-item",
    hash: sourceAction.hash,
    name: t("general.current-item"),
    conditionFields: sourceAction.description.conditionFields,
    reference: {
      oneofKind: "actionReference",
      actionReference: sourceAction.actionReference,
    },
  };
};

// Reads the same find-records limit signal as isFindRecordsOne (Forms/InputForm/utils.ts)
// so the fingerprint changes whenever eligibility could change.
const findRecordsLimitOf = (action: ActionCandidate): number | string => {
  if (action.actionReference?.actionId !== "find-records") return "";
  const limitSort = findInputAndPick(
    action.inputs,
    FIND_RECORD_LIMIT_ID,
    pickValueConfigLimitSort,
  );
  return limitSort?.limit ?? "";
};

export const useConditionLeftSources = (
  candidate: CandidateStore,
  branchRef: BranchRef,
  integrations: IntegrationDescription[],
): LeftSource[] => {
  const trigger = candidate.self.trigger;
  const upstream = candidate.branches.upstreamActionsOf(branchRef.instanceId);

  // Current Item: only when the branch block sits inside a loop whose source
  // is a single-record (find-records) entity. Resolved outside useMemo so its
  // content feeds the fingerprint below.
  const loopScope = candidate.looping.scopeForGroup(branchRef.instanceId);
  const currentItem =
    loopScope !== undefined
      ? currentItemLeftSource(candidate, loopScope)
      : undefined;

  // content fingerprint: upstream is a fresh array identity each call. Must capture
  // everything that affects the returned LeftSource[]: trigger presence, per-action
  // hash, instanceId, resolved name, and find-records limit (mirrors use-upstream-actions),
  // and the resolved Current Item source (enclosing loop id + its condition fields).
  const fingerprint = [
    trigger.triggerReference !== undefined ? "trigger" : "",
    ...upstream.map(
      (a) =>
        `${a.hash}:${a.actionReference?.instanceId ?? ""}:${
          a.actionName ?? ""
        }:${getActionName(a, integrations)}:${findRecordsLimitOf(a)}`,
    ),
    `current-item:${loopScope?.instanceId ?? ""}:${currentItem?.hash ?? ""}:${
      currentItem?.conditionFields.map((f) => f.conditionFieldId).join(",") ??
      ""
    }`,
  ].join("|");

  return useMemo(() => {
    const sources: LeftSource[] = [];

    if (trigger.triggerReference !== undefined) {
      sources.push({
        kind: "trigger",
        hash: trigger.hash,
        name: t("automation-form.fields-from-trigger"),
        conditionFields: trigger.description.conditionFields,
        reference: {
          oneofKind: "triggerReference",
          triggerReference: trigger.triggerReference,
        },
      });
    }

    for (const action of upstream) {
      const eligible = isSupportedOutput(
        action,
        upstream,
        undefined,
        undefined,
      );
      if (!eligible) continue;
      const source = actionToLeftSource(action, integrations);
      if (source !== undefined) sources.push(source);
    }

    if (currentItem !== undefined) sources.push(currentItem);

    return sources;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fingerprint keys upstream/integrations/current-item content
  }, [trigger, fingerprint]);
};
